import { useRef } from "react";
import styled from "@emotion/styled";

import type { GroupType, Option } from "./types";
import { isGroup } from "./utils";

export type ComboboxListProps = {
  items: Option[] | GroupType[];
  filteredItems: Option[] | GroupType[];
  children: React.ReactNode | ((group: GroupType, index: number) => React.ReactNode);
  ariaLabel?: string;
};

/**
 * Renders the scrollable list of combobox options.
 *
 * There are two modes, determined by whether `items` contains flat options or grouped options.
 * Using the wrong child type for the data shape will throw at runtime.
 *
 * @example
 * // Flat list — pass ReactNode children (e.g. ComboboxCollection)
 * <ComboboxList items={combobox.items} filteredItems={combobox.filteredItems}>
 *   <ComboboxCollection options={combobox.filteredItems as Option[]}>
 *     {(option) => (
 *       <ComboboxItem key={String(option.value)} option={option} ... />
 *     )}
 *   </ComboboxCollection>
 * </ComboboxList>
 *
 * @example
 * // Grouped list — pass a render function that receives each GroupType
 * <ComboboxList items={combobox.items} filteredItems={combobox.filteredItems}>
 *   {(group) => (
 *     <ComboboxGroup key={group.label} options={group.options}>
 *       {(options) => (
 *         <div key={group.label}>
 *           <ComboboxLabel>{group.label}</ComboboxLabel>
 *           <ComboboxCollection options={options}>
 *             {(option) => (
 *               <ComboboxItem key={String(option.value)} option={option} ... />
 *             )}
 *           </ComboboxCollection>
 *         </div>
 *       )}
 *     </ComboboxGroup>
 *   )}
 * </ComboboxList>
 */
export function ComboboxList({
  items,
  filteredItems,
  children,
  ariaLabel = "Select List",
}: ComboboxListProps) {
  const parentRef = useRef<HTMLDivElement>(null);

  // Check if items are grouped based on original items structure
  const isGrouped = items.length > 0 && isGroup(items[0]);
  const isRenderFunction = typeof children === "function";

  // Grouped lists - must use render function
  if (isGrouped) {
    if (!isRenderFunction) {
      throw new Error("ComboboxList with grouped items must use a render function as children");
    }

    const groups = filteredItems as GroupType[];
    const content = groups.map((group, index) => children(group, index));
    return (
      <ListContainer ref={parentRef} aria-label={ariaLabel}>
        {content}
      </ListContainer>
    );
  }

  // Simple list - must use direct children
  if (isRenderFunction) {
    throw new Error(
      "ComboboxList: When using grouped items, you must use a render function. See documentation for examples.",
    );
  }

  return (
    <ListContainer ref={parentRef} aria-label={ariaLabel}>
      {children}
    </ListContainer>
  );
}

const ListContainer = styled.div({
  overflowY: "auto",
  overflowX: "hidden",
  flex: 1,
});
